import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { usePostApiV1GroupsByGroupIdMembersMutation } from '@wellesley/group/api'
import { useState } from 'react'
import { useAppsSearchParams } from '@wellesley/router'

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const formSchema = z
  .object({
    search: z.string().trim().optional().or(z.literal('')),
    users: z
      .array(z.union([z.string(), z.object({ id: z.string() })]))
      .default([]),
  })
  .superRefine((val, ctx) => {
    const hasSearch = !!val.search?.trim()
    const hasUsers = (val.users?.length ?? 0) > 0

    if (hasSearch && !emailPattern.test(val.search!)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['search'],
        message: 'Invalid email address',
      })
    }

    if (!hasSearch && !hasUsers) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['search'],
        message: 'Please add at least one user or enter an email',
      })
    }
  })

export type FormType = z.infer<typeof formSchema>

type HookProps = {
  groupId: string
  onClose: () => void
}

const useInviteForm = ({ groupId, onClose }: HookProps) => {
  const form = useForm<FormType>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      users: [],
      search: '',
    },
  })

  const handleClose = () => {
    setSearchParams('')
    onClose()
  }

  const [addUser] = usePostApiV1GroupsByGroupIdMembersMutation()
  const [isLoading, setIsLoading] = useState(false)
  const [, setSearchParams] = useAppsSearchParams()

  // TODO: use mass add when api ready
  const onSubmit = (data: FormType) => {
    setIsLoading(true)
    const promises: Promise<unknown>[] = []

    if (data.search) {
      form.setValue('users', [...form.getValues('users'), data.search])
      form.setValue('search', '')
      data.users.push(data.search)
    }

    setTimeout(() => {
      data.users.forEach((user) => {
        const isUser = (
          item: { id: string } | string,
        ): item is { id: string } => {
          return typeof item === 'object' && 'id' in item
        }

        promises.push(
          addUser({
            groupId,
            groupMemberCreate: {
              memberId: isUser(user) ? user.id : user,
            },
          }),
        )
      })

      void Promise.all(promises).then(() => {
        handleClose()
        setIsLoading(false)
      })
    }, 2000)
  }

  return { form, onSubmit, isLoading, handleClose }
}

export default useInviteForm
